import {
  ConnectionError,
  ErrorMessage,
  MeetingCallRequest,
  MeetingExcludeRequest,
  MeetingGetByIdRequest,
  MeetingJoinRequest,
  MeetingJoinStatus,
  MeetingJoinWaitingRoomRequest,
  MeetingLeaveRequest,
  MeetingLeaveWaitingRoomRequest,
  MeetingMessageCreateRequest,
  MeetingMessageDeleteRequest,
  MeetingMessageGetRequest,
  MeetingMessageOnCreateActionHandler,
  MeetingMessageOnDeleteActionHandler,
  MeetingMessageOnReactionActionHandler,
  MeetingMessageOnUpdateActionHandler,
  MeetingMessageReactionRequest,
  MeetingMessageUpdateRequest,
  MeetingOnExcludeActionHandler,
  MeetingOnJoinActionHandler,
  MeetingOnLeaveActionHandler,
  MeetingOnUpdateActionHandler, MeetingRoomGetAllRequest,
  MeetingRoomJoinRequest,
  MeetingRoomLeaveRequest,
  MeetingSessionCustomEventRequest,
  MeetingSessionMediaOnUpdateActionHandler,
  MeetingSessionMediaUpdateRequest,
  MeetingSessionOnCustomEventActionHandler,
  MeetingSessionOnStatsActionHandler,
  MeetingSessionOnUpdateActionHandler,
  MeetingSessionPushStatsRequest,
  MeetingSessionUpdateRequest,
  MeetingUpdateByIdRequest,
  MeetingUserDataCallStatus,
  MeetingUserDataOnUpdateActionHandler,
  MeetingUserDataUpdateRequest,
  ResultEmittedMessage,
  SocketActionHandler,
  SurveyAnswerCreateRequest,
  SurveyAnswerOnCreateHandler,
  SurveyCloseRequest,
  SurveyCreateRequest,
  SurveyJoinRequest,
  SurveyLeaveRequest,
  SurveyOnUpdateHandler,
  WebSocket,
  MeetingRoomBatchOnCreateActionHandler,
  MeetingRoomBatchOnUpdateActionHandler,
  MeetingRoomOnUpdateActionHandler,
  MeetingRoomBatchCreateRequest,
  MeetingRoomEnlistRequest,
  MeetingRoomBatchUpdateRequest,
  MeetingRoomBatchCompleteRequest,
} from "@kalyzee/kast-web-sdk/websocket";
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload, Middleware, PayloadAction } from "@reduxjs/toolkit";
import { logger } from "../../helpers/logger";
import { getCompatibilityInfos, sdkClient } from "../../helpers/sdk";
import { MeetingSessionExtra } from "../../interfaces/meeting";
import socketMeetingActions from "../meeting/actions";
import {
  cleanMeeting,
  setJoinStatus,
  setMeeting,
  setRoom,
  setSession,
  setUserData,
  setWaitingRoom,
  updateMediaDestinations,
  updateSession,
  updateSessionMedia,
} from "../meeting/slices";
import { setApiRestCompatibilityInfo, setWebsocketServerCompatibilityInfo } from "../session/slices";
import { AppMiddlewareApi } from "../store";
import socketSurveyActions from "../survey/actions";
// import { addAnswer, cleanJoinedSurvey, setJoinedSurvey, updateJoinedSurvey } from "../survey/slices";
import socketUserMeetingRoomsActions from "../meeting-room/actions";
import { RTCStreamStatsResult } from "@kalyzee/kast-web-sdk/webrtc";

const log = (...args: string[]) => {
  logger.log("[SOCKET] ", ...args);
};

/// Redux Action Handlers ///

interface ActionEntryWithPayload<P = any> {
  actionCreator: ActionCreatorWithPayload<P>;
  handle: (socket: WebSocket, store: AppMiddlewareApi, payload: P) => Promise<ResultEmittedMessage | undefined> | void;
}

interface ActionEntry {
  actionCreator: ActionCreatorWithoutPayload;
  handle: (socket: WebSocket, store: AppMiddlewareApi) => void;
}

const reduxActionHandlers: (ActionEntry | ActionEntryWithPayload)[] = [
  /////////////////////
  ////// Meeting //////
  /////////////////////

  {
    actionCreator: socketMeetingActions.getById,
    handle: (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingGetByIdRequest) => socket.emitter.meeting.getById(payload),
  },
  {
    actionCreator: socketMeetingActions.join,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingJoinRequest) => {
      const result = await socket.emitter.meeting.join(payload);
      if (!result.error && result.response) {
        const { status, userData, meeting, session } = result.response;
        store.dispatch(setJoinStatus(status));
        store.dispatch(setUserData(userData));
        if (session) {
          store.dispatch(setSession(session));
          store.dispatch(updateMediaDestinations(session));
        }
        if (meeting) {
          store.dispatch(setMeeting(meeting));
        }
        if (status !== MeetingJoinStatus.ACCEPTED) {
          // We join the waiting room
          store.dispatch(
            socketMeetingActions.joinWaitingRoom({
              id: payload.id,
              medias: payload.medias,
              extra: payload.extra,
            })
          );
        }
      }
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.joinWaitingRoom,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingJoinWaitingRoomRequest) => {
      const result = await socket.emitter.meeting.joinWaitingRoom(payload);
      if (!result.error && result.response) {
        const { room, session } = result.response;
        store.dispatch(setWaitingRoom(room));
        store.dispatch(setSession(session));
      }
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.leaveWaitingRoom,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingLeaveWaitingRoomRequest) => {
      // TODO : cleanup
      return await socket.emitter.meeting.leaveWaitingRoom(payload);
    },
  },
  {
    actionCreator: socketMeetingActions.leave,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingLeaveRequest) => {
      const result = await socket.emitter.meeting.leave(payload);
      if (!result.error && result.response) {
        store.dispatch(cleanMeeting());
      }
      return result;
    },
  },
  // {
  //   actionCreator: socketMeetingActions.call,
  //   handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingCallRequest) => {
  //     const result = await socket.emitter.meeting.call(payload);
  //     if (!result.error && result.response) {
  //       const { meeting } = result.response;
  //       store.dispatch(setMeeting(meeting));
  //     }
  //     return result;
  //   },
  // },
  {
    actionCreator: socketMeetingActions.exclude,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingExcludeRequest) => {
      return await socket.emitter.meeting.exclude(payload);
    },
  },
  {
    actionCreator: socketMeetingActions.updateSessionById,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: { data: MeetingSessionUpdateRequest<MeetingSessionExtra>; storeResult?: boolean }) => {
      const result = await socket.emitter.meeting.updateSession(payload.data);
      if (!result.error && result.response && payload.storeResult) {
        store.dispatch(updateSession(result.response.session));
      }
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.publishCustomEventSession,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingSessionCustomEventRequest) => {
      return await socket.emitter.meeting.publishCustomEventSession(payload.data);
    },
  },
  {
    actionCreator: socketMeetingActions.sessionStats,
    handle: async (
      socket: WebSocket,
      store: AppMiddlewareApi,
      payload: { meetingId: string; sessionId: string; stats: { [meetingId: string]: RTCStreamStatsResult } }
    ) => {
      const data: MeetingSessionPushStatsRequest = {
        id: payload.meetingId,
        sessionId: payload.sessionId,
        stats: {
          medias: Object.keys(payload.stats).reduce<any[]>((p, c) => {
            p.push({
              mediaId: c,
              ...payload.stats[c],
            });
            return p;
          }, []),
        },
      };
      const result = await socket.emitter.meeting.pushStatsSession(data);
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.updateSessionMediaById,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: { data: MeetingSessionMediaUpdateRequest; storeResult?: boolean }) => {
      const result = await socket.emitter.meeting.updateSessionMedia(payload.data);
      if (!result.error && result.response && payload.storeResult) {
        store.dispatch(updateSessionMedia(result.response.media));
      }
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.getMessages,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: { data: MeetingMessageGetRequest; storeResult?: boolean }) => {
      const result = await socket.emitter.meeting.getMessages(payload.data);
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.createMessage,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: { data: MeetingMessageCreateRequest; storeResult?: boolean }) => {
      const result = await socket.emitter.meeting.createMessage(payload.data);
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.updateMessage,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: { data: MeetingMessageUpdateRequest; storeResult?: boolean }) => {
      const result = await socket.emitter.meeting.updateMessage(payload.data);
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.deleteMessage,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: { data: MeetingMessageDeleteRequest; storeResult?: boolean }) => {
      const result = await socket.emitter.meeting.deleteMessage(payload.data);
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.setMessageReaction,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: { data: MeetingMessageReactionRequest; storeResult?: boolean }) => {
      const result = await socket.emitter.meeting.setMessageReaction(payload.data);
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.joinRoom,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingRoomJoinRequest) => {
      const result = await socket.emitter.meeting.joinRoom(payload);
      if (!result.error && result.response) {
        const { room, session } = result.response;
        store.dispatch(setRoom(room));
        store.dispatch(setSession(session));
      }
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.leaveRoom,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingRoomLeaveRequest) => {
      const result = await socket.emitter.meeting.leaveRoom(payload);
      // TODO : cleanup
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.updateUserData,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingUserDataUpdateRequest) => {
      const result = await socket.emitter.meeting.updateUserData(payload);
      if (!result.error && result.response) {
        const { meetingId, updateData, userData } = result.response;
        // TODO : stuff ?
      }
      return result;
    },
  },
  {
    actionCreator: socketMeetingActions.updateState,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: Pick<MeetingUpdateByIdRequest, "id" | "state">) => {
      const result = await socket.emitter.meeting.updateById(payload);
      if (!result.error && result.response) {
        const { meeting } = result.response;
        store.dispatch(setMeeting(meeting));
      }
      return result;
    },
  },

  ////////////////////
  ////// Survey //////
  ////////////////////

  {
    actionCreator: socketSurveyActions.create,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: SurveyCreateRequest) => {
      const result = await socket.emitter.survey.create(payload);
      // if (!result.error && result.response) {
      //   const survey = result.response;
      //   // Auto join after creating :
      //   //store.dispatch(socketSurveyActions.join({ surveyId: survey.id }));
      // }
      return result;
    },
  },
  {
    actionCreator: socketSurveyActions.createAnswer,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: SurveyAnswerCreateRequest) => {
      const result = await socket.emitter.survey.answerCreate(payload);
      return result;
    },
  },
  // {
  //   actionCreator: socketSurveyActions.join,
  //   handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: SurveyJoinRequest) => {
  //     const result = await socket.emitter.survey.join(payload);
  //     if (!result.error && result.response) {
  //       const survey = result.response;
  //       store.dispatch(setJoinedSurvey(survey));
  //     }
  //     return result;
  //   },
  // },
  // {
  //   actionCreator: socketSurveyActions.leave,
  //   handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: SurveyLeaveRequest) => {
  //     const result = await socket.emitter.survey.leave(payload);
  //     if (!result.error && result.response) {
  //       store.dispatch(cleanJoinedSurvey());
  //     }
  //     return result;
  //   },
  // },
  {
    actionCreator: socketSurveyActions.close,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: SurveyCloseRequest) => {
      const result = await socket.emitter.survey.close(payload);
      // if (!result.error && result.response) {
      //   const survey = result.response;
      //   store.dispatch(updateJoinedSurvey(survey));
      // }
      return result;
    },
  },
  {
    actionCreator: socketSurveyActions.delete,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: SurveyCloseRequest) => {
      const result = await socket.emitter.survey.remove(payload);
      // if (!result.error && result.response) {
      //   const survey = result.response;
      //   store.dispatch(updateJoinedSurvey(survey));
      // }
      return result;
    },
  },

  ////////////////////
  /// User groups v1 (based on group)////
  ////////////////////

  // {
  //   actionCreator: socketUserGroupsActions.createBatchUserGroup,
  //   handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: UserGroupBatchCreateRequest) => {
  //     const result = await socket.emitter.userGroup.createBatch(payload);
  //     if (!result.error && result.response) {
  //       const userGroups = result.response;
  //       store.dispatch(batchRoomsUpdated(userGroups));
  //     }
  //     return result;
  //   },
  // },

  ////////////////////
  /// User groups v2 (based on meeting room)////
  ////////////////////
  {
    actionCreator: socketUserMeetingRoomsActions.createBatch,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingRoomBatchCreateRequest) => {
      const result = await socket.emitter.meeting.createRoomBatch(payload);
      return result;
    },
  },
  {
    actionCreator: socketUserMeetingRoomsActions.getBatchFromMeeting,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingRoomGetAllRequest) => {
      const result = await socket.emitter.meeting.getAllRooms(payload);
      return result
    },
  },
  {
    actionCreator: socketUserMeetingRoomsActions.enlistInRoom,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingRoomEnlistRequest) => {
      const result = await socket.emitter.meeting.enlistInRoom(payload);
      return result;
    },
  },
  {
    actionCreator: socketUserMeetingRoomsActions.delistFromRoom,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingRoomEnlistRequest) => {
      const result = await socket.emitter.meeting.delistFromRoom(payload);
      return result;
    },
  },
  {
    actionCreator: socketUserMeetingRoomsActions.updateBatch,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingRoomBatchUpdateRequest) => {
      const result = await socket.emitter.meeting.updateRoomBatch(payload);
      return result;
    },
  },
  {
    actionCreator: socketUserMeetingRoomsActions.completeBatch,
    handle: async (socket: WebSocket, store: AppMiddlewareApi, payload: MeetingRoomBatchCompleteRequest) => {
      const result = await socket.emitter.meeting.completeRoomBatch(payload);
      return result;
    },
  },
];

const reduxActionMatcher = (socket: WebSocket, store: AppMiddlewareApi, action: PayloadAction): Promise<ResultEmittedMessage | undefined> | undefined => {
  // eslint-disable-next-line no-restricted-syntax
  for (const handler of reduxActionHandlers) {
    if (handler.actionCreator.match(action)) {
      const result = handler.handle(socket, store, action.payload);
      if (result instanceof Promise) return result;
      return undefined;
    }
  }
  return undefined;
};

/// Socket Action Handlers ///

const socketActionHandlers = (store: AppMiddlewareApi): SocketActionHandler[] => {
  const handlers: SocketActionHandler[] = [];

  /////////////////////
  ////// Meeting //////
  /////////////////////

  const meetingOnJoinHandler = new MeetingOnJoinActionHandler();
  meetingOnJoinHandler.onReceive = (message) => {
    store.dispatch(socketMeetingActions.onJoin(message.content));
  };
  handlers.push(meetingOnJoinHandler);

  const meetingOnUserUpdateHandler = new MeetingUserDataOnUpdateActionHandler();
  meetingOnUserUpdateHandler.onReceive = (message) => {
    const { joinStatus, userData: currentUserData, waitingRoom, session } = store.getState().meeting;

    const { userData } = message.content;

    if (currentUserData?.id !== userData.id) return;

    store.dispatch(setUserData(userData));

    // legacy
    // if (joinStatus !== MeetingJoinStatus.ACCEPTED && userData.callStatus === MeetingUserDataCallStatus.ACCEPTED) {
    //   // The user is currently waiting to be accepted in the meeting and just was
    //   if (waitingRoom) {
    //     store.dispatch(
    //       socketMeetingActions.join({
    //         id: waitingRoom.meetingId,
    //         medias: session?.medias,
    //         extra: session?.extra,
    //       })
    //     );
    //   }
    // }
  };
  handlers.push(meetingOnUserUpdateHandler);

  const meetingOnLeaveHandler = new MeetingOnLeaveActionHandler();
  meetingOnLeaveHandler.onReceive = (message) => {
    store.dispatch(socketMeetingActions.onLeave(message.content));
  };
  handlers.push(meetingOnLeaveHandler);

  const meetingOnExcludeHandler = new MeetingOnExcludeActionHandler();
  meetingOnExcludeHandler.onReceive = (message) => {
    store.dispatch(socketMeetingActions.onExclude(message.content));
  };
  handlers.push(meetingOnExcludeHandler);

  const meetingSessionOnUpdateHandler = new MeetingSessionOnUpdateActionHandler();
  meetingSessionOnUpdateHandler.onReceive = (message) => {
    store.dispatch(updateSession(message.content.session));
    store.dispatch(socketMeetingActions.onSessionUpdate(message.content));
  };
  handlers.push(meetingSessionOnUpdateHandler);

  const meetingSessionOnCustomEventHandler = new MeetingSessionOnCustomEventActionHandler();
  meetingSessionOnCustomEventHandler.onReceive = (message) => {
    store.dispatch(socketMeetingActions.onSessionCustomEvent(message.content));
  };
  handlers.push(meetingSessionOnCustomEventHandler);

  const meetingSessionOnStatsHandler = new MeetingSessionOnStatsActionHandler();
  meetingSessionOnStatsHandler.onReceive = (message) => store.dispatch(socketMeetingActions.onSessionStats(message.content));
  handlers.push(meetingSessionOnStatsHandler);

  const meetingSessionMediaOnUpdateHandler = new MeetingSessionMediaOnUpdateActionHandler();
  meetingSessionMediaOnUpdateHandler.onReceive = (message) => {
    store.dispatch(updateSessionMedia(message.content.media));
    store.dispatch(socketMeetingActions.onSessionMediaUpdate(message.content));
  };
  handlers.push(meetingSessionMediaOnUpdateHandler);

  const meetingOnUpdateActionHandler = new MeetingOnUpdateActionHandler();
  meetingOnUpdateActionHandler.onReceive = (message) => {
    store.dispatch(setMeeting(message.content.meeting));
  };
  handlers.push(meetingOnUpdateActionHandler);

  // messages
  const meetingMessageOnCreateHandler = new MeetingMessageOnCreateActionHandler();
  meetingMessageOnCreateHandler.onReceive = (message) => {
    store.dispatch(socketMeetingActions.onCreateMessage(message.content));
  };
  handlers.push(meetingMessageOnCreateHandler);

  const meetingMessageOnUpdateHandler = new MeetingMessageOnUpdateActionHandler();
  meetingMessageOnUpdateHandler.onReceive = (message) => {
    store.dispatch(socketMeetingActions.onUpdateMessage(message.content));
  };
  handlers.push(meetingMessageOnUpdateHandler);

  const meetingMessageOnDeleteHandler = new MeetingMessageOnDeleteActionHandler();
  meetingMessageOnDeleteHandler.onReceive = (message) => {
    store.dispatch(socketMeetingActions.onDeleteMessage(message.content));
  };
  handlers.push(meetingMessageOnDeleteHandler);

  const meetingMessageOnReactionHandler = new MeetingMessageOnReactionActionHandler();
  meetingMessageOnReactionHandler.onReceive = (message) => {
    store.dispatch(socketMeetingActions.onMessageReaction(message.content));
  };
  handlers.push(meetingMessageOnReactionHandler);

  /////////////////////
  ////// Survey //////
  /////////////////////

  // const surveyAnswerOnCreateHandler = new SurveyAnswerOnCreateHandler();
  // surveyAnswerOnCreateHandler.onReceive = (message) => {
  //   store.dispatch(addAnswer(message.content));
  // };
  // handlers.push(surveyAnswerOnCreateHandler);

  // const surveyOnUpdateHandler = new SurveyOnUpdateHandler();
  // surveyOnUpdateHandler.onReceive = (message) => {
  //   store.dispatch(updateJoinedSurvey(message.content));
  // };
  // handlers.push(surveyOnUpdateHandler);

  /////////////////////////
  ////// User Rooms ///////
  /////////////////////////

  const meetingRoomOnUpdateHandler = new MeetingRoomOnUpdateActionHandler(); //
  meetingRoomOnUpdateHandler.onReceive = (message) => {
    const content = { 
      ...message.content,
      room: {
        ...message.content.room,
        audience: undefined, // If audience is defined, a redux error is raised, need to investigate why
      },                     // it's not useful anyway
    };
    store.dispatch(socketUserMeetingRoomsActions.onUpdate(content));
  }
  handlers.push(meetingRoomOnUpdateHandler);

  return handlers;
};

/// Middleware ///
export const socketMiddleware: Middleware = (store: AppMiddlewareApi) => {
  const websocketClient = sdkClient.getWebsocketClient();

  websocketClient.registerActionHandlers(socketActionHandlers(store));
  websocketClient.onConnect = async () => {
    log("[WEBSOCKETCLIENT] Connected");
    const compatibilityInfos = await getCompatibilityInfos();
    if (compatibilityInfos.apiRest) store.dispatch(setApiRestCompatibilityInfo(compatibilityInfos.apiRest));
    if (compatibilityInfos.websocketServer) store.dispatch(setWebsocketServerCompatibilityInfo(compatibilityInfos.websocketServer));
  };
  websocketClient.onDisconnect = () => {
    log("[WEBSOCKETCLIENT] Disconnected");
    store.dispatch(cleanMeeting());
  };

  websocketClient.onConnectionFailed = (error: ConnectionError) => {
    log("[WEBSOCKETCLIENT] Connection Failed");
  };

  websocketClient.onAbandon = () => {
    log("[WEBSOCKETCLIENT] Abandonned");
  };

  websocketClient.onErrorMessageCallback = (error: ErrorMessage) => {
    log(`[WEBSOCKETCLIENT] Error : ${JSON.stringify(error)}`);
  };

  // eslint-disable-next-line arrow-body-style
  return (next) => async (action) => {
    const result = reduxActionMatcher(websocketClient, store, action);
    if (result) return result;
    return next(action);
  };
};
